import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  Activity,
  TrendingDown,
  TrendingUp,
  ScrollText,
  Clock,
  Shield,
  ArrowUpRight,
  FolderKanban,
  Plus,
  Lock,
  Sparkles as SparkIcon,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import HeroBanner from "@/components/HeroBanner";
import ProjectSwitcher from "@/components/ProjectSwitcher";
import NewProjectDialog from "@/components/NewProjectDialog";
import PricingDialog from "@/components/PricingDialog";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import { Button } from "@/components/ui/button";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { useAuth } from "@/lib/AuthContext";
import { useCredits } from "@/lib/CreditsContext";
import useCountUp from "@/lib/useCountUp";
import { Skeleton } from "@/components/ui/skeleton";

const CHART = ["#06B6D4", "#3B82F6", "#F59E0B", "#10B981", "#F43F5E"];

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid #1E293B",
    borderRadius: 2,
    color: "#F8FAFC",
    fontSize: 12,
    fontFamily: "IBM Plex Mono, monospace",
  },
  labelStyle: { color: "#94A3B8" },
};

export default function Dashboard() {
  const { project, setActiveProjectId, loaded } = useActiveProject();
  const [newOpen, setNewOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const { user } = useAuth();
  const { lifetimePurchased } = useCredits();
  const data = project;

  // Hard paywall: signed-in users must purchase a plan before adding data.
  const locked = !!user && lifetimePurchased <= 0;

  // Auto-open pricing once per session for locked users
  useEffect(() => {
    if (!locked) return;
    const KEY = "threec_paywall_auto_opened";
    if (sessionStorage.getItem(KEY) === "1") return;
    sessionStorage.setItem(KEY, "1");
    setPricingOpen(true);
  }, [locked]);

  if (!data) {
    if (!loaded) {
      return (
        <div className="space-y-4" data-testid="dashboard-loading">
          <Skeleton className="h-10 w-72 bg-slate-900" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-28 bg-slate-900" />
            ))}
          </div>
        </div>
      );
    }
    if (locked) {
      return (
        <div
          data-testid="dashboard-paywall-state"
          className="min-h-[60vh] grid place-items-center"
        >
          <div className="text-center max-w-lg border border-cyan-500/40 bg-gradient-to-br from-cyan-500/10 via-slate-900/40 to-slate-950 rounded-sm p-10 shadow-[0_0_40px_-12px_rgba(6,182,212,0.4)]">
            <Lock className="h-8 w-8 text-cyan-400 mx-auto mb-4" />
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1">
              Choose your plan
            </div>
            <h2 className="font-display text-2xl font-semibold text-slate-100 mb-2">
              Pick a pricing plan to add your first project
            </h2>
            <p className="text-sm text-slate-400 mb-6">
              Every AI generation runs on real credits. Start with{" "}
              <span className="text-cyan-300 font-semibold">Starter</span> — 10
              generations for $10. Credits never expire.
            </p>
            <Button
              data-testid="dashboard-paywall-cta"
              onClick={() => setPricingOpen(true)}
              className="rounded-sm bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-6"
            >
              <SparkIcon className="h-4 w-4 mr-1.5" /> View plans
            </Button>
          </div>
          <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
        </div>
      );
    }
    return (
      <div
        data-testid="dashboard-empty-state"
        className="min-h-[60vh] grid place-items-center"
      >
        <div className="text-center max-w-md border border-slate-800 bg-slate-900/40 rounded-sm p-10">
          <FolderKanban className="h-8 w-8 text-cyan-400 mx-auto mb-4" />
          <h2 className="font-display text-xl font-semibold text-slate-100 mb-2">
            No live projects yet
          </h2>
          <p className="text-sm text-slate-400 mb-6">
            Add your first project to unlock contract, schedule and financial
            intelligence on your real data.
          </p>
          <Button
            data-testid="dashboard-empty-new-project"
            onClick={() => setNewOpen(true)}
            className="rounded-sm bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add Live Project
          </Button>
        </div>
        <NewProjectDialog open={newOpen} onOpenChange={setNewOpen} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <HeroBanner />

      {user ? <OnboardingChecklist projectExists={true} /> : null}

      <SectionHeader
        overline="Live Project Intelligence Overview"
        title={data.name}
        description="AI-driven insights across contracts, schedule and financial performance."
        right={
          <div className="flex flex-col items-end gap-2">
            <ProjectSwitcher
              currentId={data.id}
              onSelect={(id) => setActiveProjectId(id)}
            />
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              ANALYTICS LIVE · {data.contract_type}
            </span>
          </div>
        }
        testid="dashboard-header"
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Project Health"
          value={data.kpis.project_health}
          unit="/ 100"
          delta="+2.4 wow"
          deltaPositive
          icon={Activity}
          color="cyan"
          testid="kpi-health"
          delay={0}
        />
        <KpiCard
          label="Delay Risk"
          value={data.kpis.delay_risk}
          unit="%"
          delta="+5.1 vs last mo"
          deltaPositive={false}
          icon={Clock}
          color="amber"
          testid="kpi-delay"
          delay={80}
        />
        <KpiCard
          label="Cost Variance"
          value={data.kpis.cost_variance}
          unit="%"
          delta="EAC > BAC"
          deltaPositive={false}
          icon={TrendingDown}
          color="rose"
          testid="kpi-cost"
          delay={160}
          decimals={1}
        />
        <KpiCard
          label="Active Claims"
          value={data.kpis.active_claims}
          unit=""
          delta="2 time-bar warn"
          deltaPositive={false}
          icon={ScrollText}
          color="blue"
          testid="kpi-claims"
          delay={240}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Progress S-curve */}
        <Panel
          className="lg:col-span-8"
          title="Progress S-Curve"
          subtitle="Planned vs actual physical progress"
          testid="panel-progress"
        >
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.progress_curve}>
              <defs>
                <linearGradient id="planned" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="actual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06B6D4" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#06B6D4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1E293B" strokeDasharray="2 4" />
              <XAxis
                dataKey="month"
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
              />
              <YAxis
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
                unit="%"
              />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
              <Area
                type="monotone"
                dataKey="planned"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#planned)"
              />
              <Area
                type="monotone"
                dataKey="actual"
                stroke="#06B6D4"
                strokeWidth={2.5}
                fill="url(#actual)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        {/* Alerts panel */}
        <Panel
          className="lg:col-span-4"
          title="Alerts Feed"
          subtitle="Time-bar · contract · finance"
          testid="panel-alerts"
        >
          <div className="space-y-3 max-h-[280px] overflow-y-auto scroll-clean pr-1">
            {data.alerts.map((a, idx) => (
              <AlertRow key={idx} {...a} idx={idx} />
            ))}
          </div>
        </Panel>

        {/* Cost vs Budget */}
        <Panel
          className="lg:col-span-7"
          title="Cost vs Budget"
          subtitle="USD millions · by phase"
          testid="panel-cost-budget"
        >
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.cost_vs_budget} barGap={4}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="2 4" />
              <XAxis
                dataKey="phase"
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
              />
              <YAxis
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
              />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
              <Bar dataKey="budget" fill="#3B82F6" radius={0} />
              <Bar dataKey="actual" fill="#06B6D4" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        {/* Risk heatmap */}
        <Panel
          className="lg:col-span-5"
          title="Risk Heatmap"
          subtitle="Likelihood × Impact"
          testid="panel-heatmap"
        >
          <Heatmap data={data.risk_heatmap} />
        </Panel>
      </div>
    </div>
  );
}

function KpiCard({ label, value, unit, delta, deltaPositive, icon: Icon, color, testid, delay = 0, decimals = 0 }) {
  const animated = useCountUp(value, { duration: 1400, decimals });
  const accent =
    {
      cyan: "text-cyan-400",
      amber: "text-amber-400",
      rose: "text-rose-400",
      blue: "text-blue-400",
    }[color] || "text-cyan-400";
  const glow =
    {
      cyan: "shadow-[0_0_24px_-6px_rgba(6,182,212,0.45)]",
      amber: "shadow-[0_0_24px_-6px_rgba(245,158,11,0.45)]",
      rose: "shadow-[0_0_24px_-6px_rgba(244,63,94,0.45)]",
      blue: "shadow-[0_0_24px_-6px_rgba(59,130,246,0.45)]",
    }[color] || "";
  return (
    <div
      className={`fade-up bg-slate-900/60 border border-slate-800 p-4 rounded-sm relative overflow-hidden hover:border-slate-700 hover:${glow} transition-all`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>
        <Icon className={`h-4 w-4 ${accent}`} strokeWidth={2} />
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-3xl font-bold text-white tracking-tight tabular-nums">
          {decimals > 0 ? animated.toFixed(decimals) : animated}
        </span>
        {unit ? (
          <span className="text-sm text-slate-500 font-mono">{unit}</span>
        ) : null}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs">
        {deltaPositive ? (
          <TrendingUp className="h-3 w-3 text-emerald-400" />
        ) : (
          <TrendingDown className="h-3 w-3 text-rose-400" />
        )}
        <span
          className={`font-mono ${
            deltaPositive ? "text-emerald-400" : "text-rose-400"
          }`}
        >
          {delta}
        </span>
      </div>
      <div className="absolute top-0 right-0 h-1 w-12 scanline opacity-60" />
    </div>
  );
}

function Panel({ children, title, subtitle, className = "", testid }) {
  return (
    <div
      className={`bg-slate-900/60 border border-slate-800 rounded-sm p-4 ${className}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-base font-semibold text-slate-50">
            {title}
          </div>
          {subtitle ? (
            <div className="text-xs text-slate-500 font-mono">{subtitle}</div>
          ) : null}
        </div>
        <ArrowUpRight className="h-3.5 w-3.5 text-slate-600" />
      </div>
      {children}
    </div>
  );
}

function AlertRow({ level, title, detail, ts, idx }) {
  const map = {
    danger: { icon: AlertTriangle, color: "text-rose-400", bar: "bg-rose-500" },
    warning: { icon: AlertTriangle, color: "text-amber-400", bar: "bg-amber-500" },
    info: { icon: Shield, color: "text-cyan-400", bar: "bg-cyan-500" },
  };
  const m = map[level] || map.info;
  return (
    <div
      className="flex gap-3 border-l-2 pl-3 py-1 hover:bg-slate-900 transition-colors"
      style={{ borderColor: "currentColor" }}
      data-testid={`alert-row-${idx}`}
    >
      <m.icon className={`h-4 w-4 mt-0.5 ${m.color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-100 truncate">{title}</div>
        <div className="text-xs text-slate-400 mt-0.5">{detail}</div>
        <div className="text-[10px] font-mono text-slate-600 mt-1 uppercase tracking-wider">
          {ts}
        </div>
      </div>
    </div>
  );
}

function Heatmap({ data }) {
  const cellColor = (score) => {
    if (score >= 16) return "bg-rose-500/80 text-white";
    if (score >= 12) return "bg-amber-500/70 text-slate-950";
    if (score >= 8) return "bg-amber-400/40 text-amber-100";
    if (score >= 5) return "bg-cyan-500/30 text-cyan-100";
    return "bg-slate-800/60 text-slate-300";
  };
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {data.map((r, i) => (
        <div
          key={i}
          className={`heat-cell rounded-sm p-2.5 border border-slate-800 ${cellColor(r.score)} hover:scale-[1.02]`}
          data-testid={`heat-cell-${i}`}
        >
          <div className="text-[10px] font-mono uppercase tracking-wider opacity-80">
            L{r.likelihood} · I{r.impact}
          </div>
          <div className="text-xs font-semibold mt-1 leading-tight">
            {r.category}
          </div>
          <div className="text-[10px] font-mono mt-1 opacity-80">
            score {r.score}
          </div>
        </div>
      ))}
    </div>
  );
}
